import { Injectable } from '@angular/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  from,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';

export interface PlaceSuggestion {
  placeId: string;
  description: string;
  placePrediction: google.maps.places.PlacePrediction;
}

export interface PlaceSuggestionOptions {
  locationBias?: google.maps.CircleLiteral;
  sessionToken?: google.maps.places.AutocompleteSessionToken;
}

//Google bills every suggestion request, so a search box asks only once typing pauses, and only
//once there is enough text to be worth asking about.
export const placeSearchDebounceMs = 300;
export const placeSearchMinLength = 3;

@Injectable({ providedIn: 'root' })
export class PlacesAutocompleteService {

  newSessionToken(): google.maps.places.AutocompleteSessionToken {
    return new google.maps.places.AutocompleteSessionToken();
  }

  fetchSuggestions(
    input: string,
    options?: PlaceSuggestionOptions,
  ): Promise<PlaceSuggestion[]> {
    return google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      ...options,
    }).then(({ suggestions }) =>
      suggestions.map((s) => ({
        placeId: s.placePrediction.placeId,
        description: s.placePrediction.text.text,
        placePrediction: s.placePrediction,
      })),
    );
  }

  //Suggestions for a search box, fed the box's text on every input event. Text too short to search
  //emits [] without a request, an answer superseded by newer text is dropped, and a failed request
  //emits []. `options` is read per request, so it can carry the current session token and bias.
  searchAsYouType(
    text$: Observable<string>,
    options: () => PlaceSuggestionOptions = () => ({}),
  ): Observable<PlaceSuggestion[]> {
    return text$.pipe(
      map((text) => text?.trim() ?? ''),
      debounceTime(placeSearchDebounceMs),
      distinctUntilChanged(),
      switchMap((text) =>
        text.length < placeSearchMinLength
          ? of([])
          : from(this.fetchSuggestions(text, options())).pipe(catchError(() => of([]))),
      ),
    );
  }
}
